'use client';

// AppHeader.tsx
import { RefObject, useCallback, useSyncExternalStore } from 'react';
import { useRouter } from 'next/navigation';
import { Box, useMediaQuery, useTheme } from '@mui/material';

// Deep imports, not the '@/components' barrel — AppHeader renders on every
// route, and the barrel re-exports the whole component graph (charts included).
import { LanguageSelector } from '@/components/common/LanguageSelector';
import { Logo } from '@/components/common/Logo';
import { AuthButtons } from '@/components/specific/button/AuthButtons';
import { DepositButton } from '@/components/specific/button/DepositButton';
import { HowItWorksButton } from '@/components/specific/button/HowItWorksButton';
import { ReferralButton } from '@/components/specific/button/ReferralButton';
import { HamburgerMenu } from '@/components/specific/dropdown/HamburgerMenu';
import { UserSettingsMenu } from '@/components/specific/dropdown/UserSettingsMenu/UserSettingsMenu';
import { NotificationBell } from '@/components/specific/notifications/NotificationBell';
import { SearchBar } from '@/components/specific/search/SearchBar';
import { HeaderActionsSkeleton } from '../../skeletons';
import { PortfolioBalances } from './PortfolioBalances';
import { StreakProgressPill } from './StreakProgressPill';

import { PAGE_HORIZONTAL_PADDING, ROUTES } from '@/constants';

interface AppHeaderActionsProps {
  isAuthenticated: boolean;
  isLoading?: boolean;
  portfolioValue: number;
  cashBalance: number;
  isLoadingPortfolio?: boolean;
  onHowItWorksOpen: () => void;
  onLoginClick: () => void;
  onDepositModalOpen: () => void;
  onReferralModalOpen: () => void;
  isReferredUser?: boolean;
  isAuthSyncing?: boolean;
}

interface AppHeaderProps {
  searchInputRef: RefObject<HTMLDivElement>;
  isAuthenticated: boolean;
  isLoading?: boolean;
  portfolioValue: number;
  cashBalance: number;
  isLoadingPortfolio?: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearchClick: (e: React.MouseEvent | React.FocusEvent) => void;
  onSearchSubmit: (value: string) => void;
  onHowItWorksOpen: () => void;
  onLoginClick: () => void;
  onDepositModalOpen: () => void;
  onReferralModalOpen: () => void;
  isReferredUser?: boolean;
  isAuthSyncing?: boolean;
}

const GuestActions = ({
  onHowItWorksOpen,
  onLoginClick,
  isAuthSyncing,
}: Pick<AppHeaderActionsProps, 'onHowItWorksOpen' | 'onLoginClick' | 'isAuthSyncing'>) => {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
      <HowItWorksButton onClick={onHowItWorksOpen} />
      <AuthButtons onLoginClick={onLoginClick} loading={isAuthSyncing} />
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <HamburgerMenu />
      </Box>
    </Box>
  );
};

const AuthenticatedActions = ({
  portfolioValue,
  cashBalance,
  isLoadingPortfolio,
  onDepositModalOpen,
  onReferralModalOpen,
  isReferredUser,
}: Pick<
  AppHeaderActionsProps,
  | 'portfolioValue'
  | 'cashBalance'
  | 'isLoadingPortfolio'
  | 'onDepositModalOpen'
  | 'onReferralModalOpen'
  | 'isReferredUser'
>) => {
  const router = useRouter();

  const handleReferralClick = () => {
    if (isReferredUser) {
      router.push(ROUTES.REFERRALS);
    } else {
      onReferralModalOpen();
    }
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
      <DepositButton onClick={onDepositModalOpen} />
      <ReferralButton onClick={handleReferralClick} />
      <PortfolioBalances
        portfolioValue={portfolioValue}
        cashBalance={cashBalance}
        isLoading={isLoadingPortfolio}
      />
      <StreakProgressPill />
      <NotificationBell />
      <UserSettingsMenu />
    </Box>
  );
};

const HeaderActions = (props: AppHeaderActionsProps) => {
  const { isAuthenticated, isLoading, isAuthSyncing } = props;
  // Show skeleton during the very first load or if authenticated state is truly unknown
  if (isLoading && isAuthenticated) {
    return <HeaderActionsSkeleton />;
  }

  if (isAuthenticated) {
    return <AuthenticatedActions {...props} />;
  }

  return <GuestActions {...props} isAuthSyncing={isAuthSyncing || isLoading} />;
};

export const AppHeader = ({
  searchInputRef,
  isAuthenticated,
  portfolioValue,
  cashBalance,
  isLoadingPortfolio,
  searchQuery,
  onSearchChange,
  onSearchClick,
  onSearchSubmit,
  onHowItWorksOpen,
  onLoginClick,
  onDepositModalOpen,
  onReferralModalOpen,
  isReferredUser,
  isLoading,
  isAuthSyncing,
}: AppHeaderProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const mounted = useSyncExternalStore(
    useCallback(() => () => {}, []),
    () => true,
    () => false,
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: 0.75, sm: 1, md: 2 },
        px: PAGE_HORIZONTAL_PADDING,
        py: 0.75,
        minHeight: { xs: 52, sm: 56 },
        width: '100%',
        maxWidth: '100%',
        flexWrap: 'nowrap',
      }}
    >
      {/* Left cluster: logo + search. flex:1 lets it claim the free space left
          by justify-content:space-between so the search bar can actually grow
          toward its own maxWidth instead of only ever hugging its content. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { md: 2, lg: 3 },
          flex: 1,
          minWidth: 0,
        }}
      >
        <Logo />
        {mounted && isDesktop && (
          <SearchBar
            searchInputRef={searchInputRef}
            onSearchClick={onSearchClick}
            value={searchQuery}
            onChange={onSearchChange}
            onSubmit={onSearchSubmit}
          />
        )}
      </Box>
      {mounted && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexShrink: 0 }}>
          <LanguageSelector compact />
          <HeaderActions
            isAuthenticated={isAuthenticated}
            isLoading={isLoading}
            portfolioValue={portfolioValue}
            cashBalance={cashBalance}
            isLoadingPortfolio={isLoadingPortfolio}
            onHowItWorksOpen={onHowItWorksOpen}
            onLoginClick={onLoginClick}
            onDepositModalOpen={onDepositModalOpen}
            onReferralModalOpen={onReferralModalOpen}
            isReferredUser={isReferredUser}
            isAuthSyncing={isAuthSyncing}
          />
        </Box>
      )}
    </Box>
  );
};
